import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { toast } from "@/components/ui/notify";
import type { BatchResponse } from "@/types/batches.types";
import { useSetRunOrigins } from "./hooks";
import { OriginPicker } from "./OriginPicker";

/**
 * Spec 084 (FR-006c) — a origem PENDENTE.
 *
 * Pendente é um terceiro estado, não "sem origem": a batelada nasceu numa etapa que recebe material
 * de outra e ninguém estava na tela para dizer de qual batelada. Enquanto está assim, a herdeira não
 * herda nada — por isso o aviso pede uma decisão em vez de só informar, e "Nenhuma origem" é uma das
 * decisões, não um jeito de adiar.
 */
export function OriginPendingNotice({ lineId, batch }: { lineId: string; batch: BatchResponse }) {
  const [picking, setPicking] = useState(false);
  const setOrigins = useSetRunOrigins(lineId, batch.runId);
  const sugestao = batch.suggestion;

  async function salvar(originRunIds: string[], mensagem: string) {
    // Erro sobe para o toast global (MutationCache).
    await setOrigins.mutateAsync(originRunIds);
    toast.success(mensagem);
  }

  return (
    <div className="border-rd-warning-500/30 space-y-2 rounded-md border bg-rd-warning-50 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="warning" withDot>
          Origem pendente
        </Pill>
        <span className="text-[12px] text-text-secondary">
          {sugestao.length > 0
            ? "Confirme de qual batelada veio o material para esta etapa herdar as características."
            : "Nenhuma batelada a montante começou antes desta. Escolha a origem ou registre que não há."}
        </span>
      </div>
      {sugestao.length > 0 ? (
        <ul className="space-y-0.5 text-[12px] text-text-primary">
          {sugestao.map((s) => (
            <li key={s.runId} className="truncate">
              <span className="text-text-tertiary">{s.upstreamLineName} · </span>
              <span className="font-mono">{s.batchCode}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {sugestao.length > 0 ? (
          <Button
            size="sm"
            className="h-7 px-2 text-[12px]"
            disabled={setOrigins.isPending}
            onClick={() =>
              void salvar(
                sugestao.map((s) => s.runId),
                "Origem confirmada."
              ).catch(() => undefined)
            }
          >
            Confirmar
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-[12px]"
          disabled={setOrigins.isPending}
          onClick={() => setPicking(true)}
        >
          {sugestao.length > 0 ? "Escolher outra" : "Escolher origem"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[12px]"
          disabled={setOrigins.isPending}
          onClick={() => void salvar([], "Batelada registrada sem origem.").catch(() => undefined)}
        >
          Nenhuma origem
        </Button>
      </div>
      {picking ? (
        <OriginPicker lineId={lineId} batch={batch} onClose={() => setPicking(false)} />
      ) : null}
    </div>
  );
}
